import {
  BasicInformationFragment,
  ExpensesCustomizationExpenseTaxesTypesEnum,
} from 'generated/resources/graphql'
import { v4 as uuid } from 'uuid'

import { Employee } from 'modules/core/hooks/useCurrentSession'

import { getBasicDetailsDefaultValues } from 'modules/expenses/components/expenses/Detail/_newForm/BasicDetails/form/form'
import { getAdditionalInformationAttributes } from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/AdditionalInformation/form/form'
import { getBasicInformationDefaultValues } from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/BasicInformation/form/form'
import { getBudgetDefaultValues } from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/Budget/form/form'
import { getCategoryDefaultValues } from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/Category/form/form'
import { getCostCenterDefaultValues } from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/CostCenters/form/form'
import { getGroupDefaultValues } from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/Group/form/form'
import { getPaymentInformationDefaultValues } from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/PaymentInformation/form/form'
import { getProjectsDefaultValues } from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/Projects/form/form'
import { getTaxesDefaultValues } from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/Taxes/form/form'
import { getVendorAttributes } from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/Vendor/form/form'
import { getFilesDefaultValues } from 'modules/expenses/components/expenses/Detail/_newForm/Files/form/form'
import { CustomizationField, fieldType } from 'modules/expenses/hooks/useExpensesCustomization'
import { Expensable } from 'modules/expenses/types/Expensable'

export type ExpenseFormValues = ReturnType<typeof getExpensesFormDefaultValues>

export interface GetExpensesFormDefaultValuesParams {
  employee: Employee
  customizationFields: CustomizationField[]
  expensable?: Expensable
  groupId?: number | null
  groupCurrency?: string | null
}

export const getExpensesFormDefaultValues = ({
  employee,
  customizationFields,
  expensable,
  groupId,
  groupCurrency,
}: GetExpensesFormDefaultValuesParams) => {
  const basicDetails = {
    expense: {
      documentType: expensable?.expense?.documentType ?? 'receipt',
    },
  }

  const basicInformation: BasicInformationFragment = {
    amount: expensable?.amount || 0,
    currency: expensable?.currency || groupCurrency || employee?.legalEntity?.currency || 'EUR',
    effectiveOn: expensable?.effectiveOn || new Date().toISOString(),
    expense: {
      documentNumber: expensable?.expense?.documentNumber ?? '',
    },
    owner: {
      employeeName: expensable?.owner?.employeeName,
    },
  }

  const categoryInformation = {
    expense: {
      category: { id: expensable?.expense?.category?.id ?? '' },
      subcategory: expensable?.expense?.subcategory ?? '',
    },
  }

  const additionalInformation = {
    internalReference: expensable?.internalReference ?? '',
    description: expensable?.description ?? '',
  }

  const vendorInformation = {
    expense: {
      merchantName: expensable?.expense?.userMerchant ?? expensable?.expense?.merchantName ?? '',
      merchantTin: expensable?.expense?.merchantTin ?? '',
    },
  }

  const paymentInformation = {
    expense: {
      payment: expensable?.expense?.payment ?? 'reimbursable',
      paymentMethod: expensable?.expense?.paymentMethod ?? '',
      creationType: expensable?.expense?.creationType ?? 'manual',
    },
    amount: basicInformation.amount,
    currency: basicInformation.currency,
    reimbursableAmount: expensable?.reimbursableAmount || basicInformation.amount,
    reimbursableCurrency: expensable?.reimbursableCurrency || basicInformation.currency,
  }

  const taxesFieldType = fieldType(customizationFields, 'expense', 'taxes')
  const defaultSingleTaxes = [
    {
      id: uuid(),
      base_amount: undefined,
      percentage: undefined,
      amount: undefined,
      // eslint-disable-next-line no-type-assertion/no-type-assertion
      type: 'vat' as ExpensesCustomizationExpenseTaxesTypesEnum,
    },
  ]

  const existingTaxes = expensable?.expense?.taxes || []

  const taxesInformation = {
    expense: {
      taxes:
        taxesFieldType === 'cents'
          ? existingTaxes.length > 0
            ? existingTaxes
            : defaultSingleTaxes
          : [...existingTaxes],
    },
  }
  const projectsInformation = {
    projects: [...(expensable?.projects || [])],
  }

  const budgetInformation = {
    bugdetMovement: {
      budget: {
        id: expensable?.bugdetMovement?.budget?.id ?? 0,
      },
    },
  }

  const files = {
    expense: {
      files: expensable?.expense?.files || [],
    },
  }

  return {
    employeeId: employee.id,
    ...getBasicDetailsDefaultValues(basicDetails),
    ...getBasicInformationDefaultValues(employee, basicInformation),
    ...getCategoryDefaultValues(categoryInformation),
    ...getAdditionalInformationAttributes(additionalInformation),
    ...getVendorAttributes(vendorInformation),
    ...getPaymentInformationDefaultValues(employee, groupId, paymentInformation, groupCurrency),
    ...getTaxesDefaultValues(taxesInformation),
    ...getProjectsDefaultValues(projectsInformation),
    ...getBudgetDefaultValues(budgetInformation),
    ...getFilesDefaultValues(files),
    ...getGroupDefaultValues(groupId),
    ...getCostCenterDefaultValues(expensable),
  }
}
